import axios from 'axios';
import { isNative } from '@/services/platform';
import { tokenStore } from '@/services/tokenStore';

// ─── API Base-URL ────────────────────────────────────────────────────────────
//
// Auflösungsreihenfolge für native iOS-App:
//   1. VUE_APP_NATIVE_API_URL  ← wird zur Build-Zeit eingebacken (aus .env.local)
//                                 → für lokale Simulator-Tests: http://<Mac-IP>:3000/v1
//   2. Produktions-Fallback    ← https://api.dash.frndsec.de/v1
//
// Für den Browser-Build:
//   window.__APP_CONFIG__ → VUE_APP_API_BASE_URL → localhost-Fallback (unverändert)
//
// Hinweis: process.env-Werte werden zur Build-Zeit eingebacken — für Dev-Builds
// einfach .env.local anlegen (ist gitignored). Für Produktions-Build weglassen.
//
const NATIVE_API_URL =
  process.env.VUE_APP_NATIVE_API_URL ||
  'https://api.dash.frndsec.de/v1';

const runtimeConfig = (() => {
  if (typeof window === 'undefined') return {};
  return (
    window.__APP_CONFIG__ ||
    window.__ENV__ ||
    window.__APP_RUNTIME__ ||
    {}
  );
})();

const baseURL = isNative
  ? NATIVE_API_URL
  : (runtimeConfig.API_BASE_URL ||
     process.env.VUE_APP_API_BASE_URL ||
     'http://localhost:3000/v1');

// Axios-Instanz erstellen
const api = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // wichtig für Cookie-basierte Authentifizierung
  // Timeout: verhindert ewiges Hängen bei nicht erreichbarer API (z.B. falsche IP im Simulator)
  timeout: 15000, // 15 Sekunden
});

// Request Interceptor — tokenStore.getSync() ist nach init() synchron und sicher
api.interceptors.request.use(
  (config) => {
    const token = tokenStore.getSync();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response Interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const { response, config } = error;

    if (response && response.status === 401) {
      // Auth-Endpoints selbst (Login, Password-Reset) liefern 401 bei falschen
      // Credentials — das ist kein Session-Ablauf, also KEIN Token löschen und
      // KEIN Redirect. Der Fehler soll normal zum aufrufenden catch() propagieren.
      const url = config?.url || '';
      const isAuthEndpoint = url.includes('/auth/');
      if (isAuthEndpoint) {
        return Promise.reject(error);
      }

      console.warn("401 Unauthorized – cleaning up token and redirecting...");

      // tokenStore.clear() setzt _cachedToken = null synchron.
      // Die Storage-Bereinigung (Keychain/localStorage) läuft im Hintergrund —
      // kein await, damit der Interceptor nicht hängt falls die Bridge langsam ist.
      tokenStore.clear();

      // Mit createWebHashHistory liegt die Route im Hash-Fragment (#/login).
      // Kein Redirect wenn bereits auf Login, um Redirect-Loops zu verhindern.
      const isOnLogin =
        window.location.hash.includes('/login') ||
        window.location.pathname.includes('/login');

      if (!isOnLogin) {
        window.location.hash = '#/login';
      }
    }

    return Promise.reject(error);
  }
);

export default api;
